import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calendar, Plus, Trash2, Sparkles, ShieldCheck } from "lucide-react";

function startOfWeek(d) {
  const nd = new Date(d);
  const day = (nd.getDay() + 6) % 7; // Monday=0
  nd.setHours(0, 0, 0, 0);
  nd.setDate(nd.getDate() - day);
  return nd;
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 8); // 8..19

export default function DoctorAgenda() {
  const { user, refresh } = useAuth();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [slots, setSlots] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ date: new Date().toISOString().slice(0, 10), start_hour: 9, end_hour: 18, duration_min: 30 });

  const load = async () => {
    const from = new Date(weekStart);
    const to = new Date(weekStart); to.setDate(to.getDate() + 7);
    try {
      const { data } = await api.get("/slots", { params: { from_date: from.toISOString(), to_date: to.toISOString() } });
      setSlots(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  });

  const addSlot = async (day, hour) => {
    const start = new Date(day);
    start.setHours(hour, 0, 0, 0);
    try {
      await api.post("/slots", { start: start.toISOString(), duration_min: 30 });
      toast.success("Créneau ajouté");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const removeSlot = async (id) => {
    try {
      await api.delete(`/slots/${id}`);
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const bulkCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/slots/bulk", bulkForm);
      toast.success(`${data.length} créneaux créés`);
      setBulkOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const slotsByDayHour = {};
  slots.forEach((s) => {
    const d = new Date(s.start);
    const key = `${d.toDateString()}|${d.getHours()}`;
    if (!slotsByDayHour[key]) slotsByDayHour[key] = [];
    slotsByDayHour[key].push(s);
  });

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      {/* RPPS verified badge + Premium card */}
      <div className="mb-6 grid lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user?.rpps_verified ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Pro Santé Connect</div>
            <div className="font-display font-bold">
              {user?.rpps_verified ? "Praticien vérifié" : "Non vérifié"}
              {user?.rpps && <span className="text-xs text-slate-500 ml-2 font-mono">RPPS {user.rpps}</span>}
            </div>
          </div>
        </div>
        {!user?.saas_plan && (
          <div className="lg:col-span-2 bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white rounded-xl p-5 flex items-center justify-between gap-4" data-testid="saas-upsell-banner">
            <div className="flex items-center gap-3">
              <Sparkles className="h-7 w-7 text-[#FFE4B5]" />
              <div>
                <div className="font-display font-extrabold text-lg">Passez au cabinet augmenté · dès 69 € HT/mois</div>
                <div className="text-sm text-white/80">30 jours d'essai · prélèvement SEPA via GoCardless · résiliable à tout moment</div>
              </div>
            </div>
            <Link to="/tarifs"><Button className="btn-coral font-bold" data-testid="saas-upsell-btn">Voir les plans</Button></Link>
          </div>
        )}
        {user?.saas_plan && (
          <div className="lg:col-span-2 bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-center justify-between gap-3" data-testid="saas-active-banner">
            <div className="flex items-center gap-3">
              <Sparkles className="h-6 w-6 text-emerald-700" />
              <div>
                <div className="font-display font-extrabold text-emerald-900">Plan {user.saas_plan === "premium" ? "Premium" : "Standard"} actif</div>
                <div className="text-sm text-emerald-800">{user.saas_status === "trialing" ? "Période d'essai en cours — aucun prélèvement avant 30 jours." : "Toutes les fonctionnalités de votre plan sont activées."}</div>
              </div>
            </div>
            <Link to="/tarifs"><Button variant="outline" size="sm" data-testid="saas-manage-btn">Gérer</Button></Link>
          </div>
        )}
      </div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-2">Mon agenda</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Disponibilités</h1>
          <p className="text-slate-500 mt-2">Cliquez sur une case pour créer un créneau, ou utilisez le remplissage automatique.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })} data-testid="agenda-prev-week">←</Button>
          <div className="px-3 text-sm text-slate-600 font-medium">
            Semaine du {weekStart.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
          </div>
          <Button variant="outline" onClick={() => setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })} data-testid="agenda-next-week">→</Button>
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <Button className="btn-primary ml-2" onClick={() => setBulkOpen(true)} data-testid="agenda-bulk-btn"><Calendar className="h-4 w-4 mr-2" /> Remplissage auto</Button>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Remplissage automatique</DialogTitle></DialogHeader>
              <form onSubmit={bulkCreate} className="space-y-4 mt-2">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={bulkForm.date} onChange={(e) => setBulkForm({ ...bulkForm, date: e.target.value })} className="mt-1.5" data-testid="bulk-date-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Début</Label>
                    <Input type="number" min="6" max="22" value={bulkForm.start_hour} onChange={(e) => setBulkForm({ ...bulkForm, start_hour: parseInt(e.target.value) })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Fin</Label>
                    <Input type="number" min="6" max="23" value={bulkForm.end_hour} onChange={(e) => setBulkForm({ ...bulkForm, end_hour: parseInt(e.target.value) })} className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label>Durée par consultation</Label>
                  <Select value={String(bulkForm.duration_min)} onValueChange={(v) => setBulkForm({ ...bulkForm, duration_min: parseInt(v) })}>
                    <SelectTrigger className="mt-1.5" data-testid="bulk-duration-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="45">45 min</SelectItem>
                      <SelectItem value="60">60 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="bulk-submit-btn">Générer les créneaux</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] border-b border-slate-200 bg-slate-50">
          <div></div>
          {days.map((d) => (
            <div key={d.toISOString()} className="p-3 text-center border-l border-slate-200">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{d.toLocaleDateString("fr-FR", { weekday: "short" })}</div>
              <div className="font-display font-semibold">{d.getDate()}</div>
            </div>
          ))}
        </div>
        <div>
          {HOURS.map((h) => (
            <div key={h} className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] border-b border-slate-100">
              <div className="p-2 text-xs text-slate-400 text-right pr-3">{h}:00</div>
              {days.map((d) => {
                const k = `${d.toDateString()}|${h}`;
                const dayHourSlots = slotsByDayHour[k] || [];
                return (
                  <div key={k} className="border-l border-slate-100 min-h-[60px] p-1 relative group hover:bg-slate-50">
                    {dayHourSlots.map((s) => (
                      <div key={s.slot_id} className={`agenda-slot text-[10px] p-1.5 mb-1 rounded border ${s.status === "booked" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`} data-testid={`slot-${s.slot_id}`}>
                        <div className="flex justify-between items-center">
                          <span className="font-semibold">{new Date(s.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                          {s.status === "available" && (
                            <button onClick={() => removeSlot(s.slot_id)} className="opacity-0 group-hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
                          )}
                        </div>
                        <div className="text-[9px]">{s.status === "booked" ? "Réservé" : `${s.duration_min}min`}</div>
                      </div>
                    ))}
                    {dayHourSlots.length === 0 && (
                      <button onClick={() => addSlot(d, h)} data-testid={`add-slot-${d.getDate()}-${h}`} className="opacity-0 group-hover:opacity-100 absolute inset-0 flex items-center justify-center text-slate-400 hover:text-[#0FACD7]">
                        <Plus className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
